import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { CepStatus } from 'basics/enums/maya.enums';
import useTacticOptions from 'basics/options/tactic.options';
import { CepStateType, TacticStateType } from 'basics/types/maya.types';
import { TacticGroupOptionType } from 'basics/types/tacticGroups.types';
import { Channel as ChannelType } from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';

const useTacticGroups = (channelKey: string, tacticGroupLoading: boolean) => {
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;
  const cepId = cepStateValue.cep?._id || null;
  const tacticId = tacticStateValue.tactic?._id ? [tacticStateValue.tactic?._id] : [];
  const tacticOptions = useTacticOptions(cepId, tacticId, [ChannelType.group]);
  const [tacticGroupsOptions, setTacticGroupsOptions] = useState<TacticGroupOptionType[]>(tacticOptions);
  const [showTacticGroup, setShowTacticGroup] = useState(false);
  const [tacticGroupDisabled, setTacticGroupDisabled] = useState(false);

  useEffect(() => {
    if (cepStateValue.tacticGroups.length) {
      const updatedTacticGroupOptions: TacticGroupOptionType[] = cepStateValue.tacticGroups.map((group) => ({
        id: group._id,
        label: group.title,
      }));

      setTacticGroupsOptions(updatedTacticGroupOptions);
    }

    const allTactics = cepStateValue.tactics;
    const parentTacticIDs = allTactics.filter((tactic) => tactic.parentTactic?._id).map((tactic) => tactic.parentTactic?._id);
    const isParentTactics = Boolean(allTactics.find((tactic) => parentTacticIDs.includes(tactic._id)));

    setShowTacticGroup(!isParentTactics);
  }, [cepStateValue.tacticGroups, cepStateValue.tactics, channelKey]);

  useEffect(() => {
    const isNewTactic = !tacticStateValue.tactic;
    const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;
    const now = new Date();
    const cepStartDate = new Date(cepStateValue.cep?.dateStart);
    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const startedCEP = cepStartDate <= todayUTC;
    const isStandaloneTactic = tacticStateValue.tactic && !tacticStateValue.tactic.parentTactic;
    const tacticGroupElementDisabled = ((isNewTactic || isStandaloneTactic) && startedCEP && isCepApproved) || tacticGroupLoading;
    setTacticGroupDisabled(tacticGroupElementDisabled);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tacticGroupLoading, tacticStateValue.tactic]);

  return {
    tacticGroupsOptions,
    showTacticGroup,
    tacticGroupDisabled,
  };
};

export default useTacticGroups;
